import { queryOptions } from "@tanstack/react-query";
import { gql } from "graphql-request";

import { gqlClient } from "../../../shared/lib/gql-client";
import { IMAGE_FIELDS } from "../../images/api/fragments";
import { LINK_FIELDS } from "../../links/api/fragments";
import type { TagMatch } from "../../tags/types";
import { OUTLOOK_FIELDS } from "../../weather/api/queries";
import type { AttendanceStatus, EventListItem } from "../types";
import type { Paged } from "../../../shared/lib/paging";

/**
 * Every field the list and detail views need, kept in one place.
 *
 * Exported so the mutation payloads reuse it rather than keeping a parallel
 * copy — a second list is a second thing to forget to update, and it had already
 * drifted (missing `tags`) before this was shared.
 */
export const EVENT_FIELDS = gql`
  ${OUTLOOK_FIELDS}
  ${LINK_FIELDS}
  ${IMAGE_FIELDS}
  fragment EventFields on Event {
    id
    title
    description
    startsAt
    endsAt
    tagSlugs
    # Resolved from tagSlugs against the vocabulary — no extra query per row.
    # Needed for the human-readable label; tagSlugs alone would render "live-music".
    tags {
      id
      slug
      label
    }
    ticketUrl
    isMine
    isCancelled
    isException
    # Who is putting it on. Two batched queries server-side regardless of page
    # size: one for the involvements, one for the organisers they name.
    organisers {
      role
      organiser {
        id
        name
        defaultVenueId
        defaultAreaId
      }
    }
    # ⚠️ Both RESOLVE THROUGH THE SERIES: this event's own rows if it has any,
    # else its series'. Resolved per type and independently — an occurrence with
    # its own images still inherits the series' links.
    #
    # The ticketUrl field above is deliberately separate from links: it stays the
    # canonical ticket link rather than becoming a list scan.
    links {
      ...LinkFields
    }
    images {
      ...ImageFields
    }
    # The viewer's own mark, and the totals. Batched server-side: both counts
    # come from one grouped query per request, so asking for all three is one
    # round trip rather than three.
    myAttendance
    interestedCount
    attendingCount
    # The series this occurrence came from, batched server-side per request.
    series {
      id
      title
      rrule
      startsAtFirst
      until
      durationMinutes
      isMine
      isCancelled
      # The series' OWN rows, which is not the same as the images and links
      # above — those already resolved the override. Selected so the edit UI can
      # say whether this night is inheriting or has overridden, which the server
      # deliberately exposes no flag for: it is derivable, and a flag would be a
      # second source of truth for the same fact.
      #
      # Both, because the override is per type: a night can carry its own poster
      # while still inheriting the run's links.
      images {
        ...ImageFields
      }
      links {
        ...LinkFields
      }
    }
    # Batched server-side per request, like series.
    venue {
      id
      name
      addressLine
      neighbourhood
      isOutdoor
      areaId
      area {
        id
        name
        # Load-bearing: the row renders start and end times in the venue's zone,
        # not the reader's, so an overnight club night reads correctly anywhere.
        timezone
      }
    }
    # A real forecast inside the 9-day horizon, typical conditions beyond it.
    # Null only when the event resolves to no area at all — genuinely placeless, or
    # a venue with no area assigned. Note "no venue" is no longer one of those
    # cases. Batched per area server-side, so a 40-event page costs one cache read
    # rather than 40.
    outlook {
      ...OutlookFields
    }
    # Where this event is, for weather and its local day. The venue's area when it
    # has one, otherwise the event's own — set when the room is unannounced but the
    # city is known. Batched server-side, and shared with Venue.area's loader.
    area {
      id
      name
      timezone
    }
    price {
      minorAmount
      currency
    }
  }
`;

const EVENTS = gql`
  ${EVENT_FIELDS}
  query Events($filter: EventFilter, $limit: Int, $offset: Int) {
    events(filter: $filter, limit: $limit, offset: $offset) {
      ...EventFields
    }
  }
`;

/**
 * The paginated listing.
 *
 * Alongside `EVENTS` rather than replacing it: that query is still what the week
 * view and the "my events" list want, where the set is already bounded by a date
 * range or by ownership and there is nothing to page through. This one is for
 * the flat listing, which is not bounded by anything.
 */
const EVENT_PAGE = gql`
  ${EVENT_FIELDS}
  query EventPage($filter: EventFilter, $limit: Int, $offset: Int) {
    eventPage(filter: $filter, limit: $limit, offset: $offset) {
      items {
        ...EventFields
      }
      totalCount
    }
  }
`;

const EVENT = gql`
  ${EVENT_FIELDS}
  query Event($id: ID!) {
    event(id: $id) {
      ...EventFields
    }
  }
`;

/** Mirrors the server's `EventFilter` input. */
export interface EventFilterInput {
  tagSlugs?: string[];
  tagMatch?: TagMatch;
  includeCancelled?: boolean;
  createdByMe?: boolean;
  /**
   * Statuses the viewer has marked. Resolves against the session server-side —
   * there is deliberately no `userId` filter, so this can only ever mean "mine".
   */
  attendance?: AttendanceStatus[];
  /**
   * Restrict to one weather area, by slug.
   *
   * Matches on the event's **effective** area — its venue's, or its own when it has
   * no venue. So a Copenhagen social whose room is unannounced is included; only a
   * genuinely placeless event is not.
   */
  areaSlug?: string;
  /**
   * Inclusive local-date bounds, `YYYY-MM-DD` in the **area's** timezone.
   *
   * Plain date strings, not instants: sending a timestamp derived from the
   * viewer's zone is exactly the bug area-relative bucketing exists to prevent.
   * Ignored server-side without `areaSlug`, since there is no zone to resolve
   * them against.
   */
  fromLocalDate?: string;
  toLocalDate?: string;
  /**
   * Hide events that have already finished. **The server defaults this to true**,
   * so omitting it gives an upcoming-only listing.
   *
   * Measured against `endsAt` where there is one, so an event mid-way through is
   * still upcoming. Screens that are a record rather than a discovery view (my
   * events, the review queue, the week view) pass `false` explicitly.
   */
  upcomingOnly?: boolean;
  /**
   * Inclusive lower / exclusive upper bounds on the start, as ISO instants.
   *
   * The no-area counterpart to the local-date pair above: instants because there
   * is no area timezone to resolve a bare date against, so the client converts
   * using its own zone — which is the right one when the listing is not
   * area-scoped and the reader is picking dates off their own calendar.
   */
  startsFrom?: string;
  startsBefore?: string;
}

export interface EventsFilter {
  filter?: EventFilterInput;
  limit?: number;
  offset?: number;
}

/**
 * queryOptions rather than a bare key factory: it restores excess-property
 * checking (a typo'd `stallTime` is silently ignored in a plain extracted
 * object) and brands the key with the return type, so getQueryData is typed
 * without a manual generic.
 *
 * Keys run generic → specific so fuzzy invalidation works:
 * invalidating `lists()` hits every list variant.
 */
export const eventQueries = {
  all: () => ["events"] as const,
  lists: () => [...eventQueries.all(), "list"] as const,
  list: (filter: EventsFilter = {}) =>
    queryOptions({
      queryKey: [...eventQueries.lists(), filter] as const,
      queryFn: () =>
        gqlClient.request<{ events: EventListItem[] }>(EVENTS, filter),
    }),
  /**
   * One page of the listing, with the total matching the filter.
   *
   * Keyed under `lists()` like `list`, so a mutation invalidating the list
   * prefix clears every page — which is right: creating an event can change
   * what is on any of them.
   */
  page: (variables: EventsFilter = {}) =>
    queryOptions({
      queryKey: [...eventQueries.lists(), "page", variables] as const,
      queryFn: () =>
        gqlClient.request<{ eventPage: Paged<EventListItem> }>(
          EVENT_PAGE,
          variables,
        ),
      /*
       * Keeps the current page visible while the next loads, rather than
       * dropping to the route's suspense fallback and blanking the screen
       * between pages.
       */
      placeholderData: (previous) => previous,
    }),

  details: () => [...eventQueries.all(), "detail"] as const,
  detail: (id: string) =>
    queryOptions({
      queryKey: [...eventQueries.details(), id] as const,
      queryFn: () =>
        gqlClient.request<{ event: EventListItem | null }>(EVENT, { id }),
      /*
       * The edit form reads this query for its defaultValues. A background
       * refetch cannot be shown in an uncontrolled form without discarding
       * whatever the user has typed, so don't refetch on focus/mount — only
       * explicit invalidation after a mutation updates it.
       */
      staleTime: Infinity,
    }),
};
